// Section 13 (stream M4a): the state-driven terms of crime propensity per adult per season,
//   base 1% (value.ts) x hunger 2x if short x inequality (1 + food Gini) x trust 2x below 40 or
//   0.5x above 80 x settlement trust below 40 2x (Section 16) x idle 1.5x (unassigned 4+ seasons)
//   x belonging short two seasons running 1.3x (Section 6) x 1 / deterrence.
// Deterrence is its own target, a strength with base 1 (value.ts): 1 + 0.15 x safety points (the
// guard job), justice responses of the last 4 seasons (the justice policy) and the harsh justice
// norm 1.3x. Crime is divided by it, so 3 guards and a watch post in a settlement of 40 put crime
// at about 0.6x (E1-02). Traits (Volatile 2x when trust is low), the guard mandate and the merciful
// justice norm (1.2x) add their own rows. Constants: config.crime, config.needs, config.trust.
import { defineContributor } from '../../core/registry';
import { collect, evaluate, mod } from '../../core/modifiers';
import { memoByState } from '../../core/memo';
import { latestReport, needsOf, settlementTrust } from '../../core/queries';
import type { Modifier, PersonId, ReadContext } from '../../core/types';

/** Deterrence strength (base 1, value.ts), once per state. */
export function deterrenceStrength(ctx: ReadContext): number {
  return memoByState(
    ctx.state,
    'crime:deterrence',
    () => evaluate(1, collect(ctx, { kind: 'deterrence' })).value,
  );
}

/** Section 6: belonging short this season and the one before (crime 1.3x). */
function lonelyTwoSeasons(ctx: ReadContext, id: PersonId): boolean {
  const s = ctx.state;
  const short = ctx.cfg.needs.shortThreshold;
  const n = s.history.length;
  const [now, before] =
    s.currentReport !== null
      ? [s.currentReport, s.history[n - 1]]
      : [s.history[n - 1], s.history[n - 2]];
  const a = now?.needs[id]?.belonging;
  const b = before?.needs[id]?.belonging;
  return a !== undefined && b !== undefined && a < short && b < short;
}

export const crimeContributor = defineContributor({
  slot: 'needs',
  id: 'crime:propensity',
  targets: ['crimeRisk'],
  modifiers(ctx, target) {
    if (target.kind !== 'crimeRisk') return [];
    const p = ctx.state.people[target.personId];
    if (p === undefined) return [];
    const cfg = ctx.cfg;
    const c = cfg.crime;
    const out: Modifier[] = [];
    const food = needsOf(ctx.state, p.id).food;
    if (food < cfg.needs.shortThreshold)
      out.push(mod.mul('needs:hungry', c.hungerMul, `fed ${Math.round(food * 100)}%`));
    if (lonelyTwoSeasons(ctx, p.id))
      out.push(mod.mul('needs:belonging', cfg.needs.belongingCrimeMul, 'lonely two seasons'));
    const gini = latestReport(ctx.state)?.gini.food ?? 0;
    if (gini > 0)
      out.push(mod.mul('inequality:food', 1 + gini, `food Gini ${Math.round(gini * 100) / 100}`));
    if (p.trust < c.lowTrustBelow)
      out.push(mod.mul('trust:low', c.lowTrustMul, `trust ${Math.round(p.trust)}`));
    else if (p.trust > c.highTrustAbove)
      out.push(mod.mul('trust:high', c.highTrustMul, `trust ${Math.round(p.trust)}`));
    const settlement = memoByState(ctx.state, 'settlement-trust', () => settlementTrust(ctx.state));
    if (settlement < cfg.trust.crimeAndEmigrationBelow) {
      out.push(
        mod.mul(
          'trust:settlement',
          c.lowSettlementTrustMul,
          `settlement trust ${Math.round(settlement)}`,
        ),
      );
    }
    if (p.job === 'rest' && p.streaks.idle >= c.idleSeasons)
      out.push(mod.mul('idle:long', c.idleMul, `idle ${p.streaks.idle} seasons`));
    const strength = deterrenceStrength(ctx);
    if (strength > 0 && strength !== 1) {
      out.push(
        mod.mul('deterrence:all', 1 / strength, `deterrence ${Math.round(strength * 100) / 100}x`),
      );
    }
    return out;
  },
});
